import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useIntlayer } from 'react-intlayer';

import { LocalizedLink } from '~/components/LocalizedLink';
import { Button } from '~/components/ui/button';
import { ArrowUpRight, Chevron, DropdownArrow } from '~/components/ui/icons';
import { SOCIAL_LINKS } from '~/constants';
import { type MenuNavItemKey } from '~/constants/navigation';
import { openExternal } from '~/utils/open-external';

/* ── Motion tokens ──────────────────────────────── */
const EASE = [0.16, 1, 0.3, 1] as const;

/* ── Checkerboard grid config ────────────────────── */
const GRID_COLS = 5;
const GRID_ROWS = 10;
const BAND_ROWS = 4;
const SWEEP_DURATION = 0.5;

/**
 * Build a shuffled delay map so tiles within each ~4-row band
 * appear in random order while the band sweeps bottom → top (open)
 * or top → bottom (close).
 */
function buildDelayMap(cols: number, rows: number, opening: boolean): number[] {
  const total = cols * rows;
  const delays = new Array<number>(total);
  const rowDelay = SWEEP_DURATION / rows;
  const bandSpread = BAND_ROWS * rowDelay;

  const rowBuckets: number[][] = Array.from({ length: rows }, () => []);
  for (let i = 0; i < total; i++) {
    rowBuckets[Math.floor(i / cols)].push(i);
  }

  for (let r = 0; r < rows; r++) {
    const sweepRow = opening ? rows - 1 - r : r;
    const bandBase = r * rowDelay;

    for (const idx of rowBuckets[sweepRow]) {
      delays[idx] = bandBase + Math.random() * bandSpread;
    }
  }

  return delays;
}

/** Build SVG path "d" string for visible tiles */
function buildClipPathD(
  cols: number,
  rows: number,
  tileW: number,
  tileH: number,
  visible: boolean[]
): string {
  let d = '';
  for (let i = 0; i < cols * rows; i++) {
    if (!visible[i]) continue;
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = col * tileW;
    const y = row * tileH;
    d += `M ${x} ${y} L ${x + tileW} ${y} L ${x + tileW} ${y + tileH} L ${x} ${y + tileH} Z `;
  }
  return d || 'M 0 0';
}

const PRIMARY_NAV_ITEMS = [
  { key: 'home', to: '/' },
  { key: 'chorna', to: '/products/chorna' },
  { key: 'tacticalDrones', to: '/products/tactical-drones' },
  { key: 'jade', to: '/products/iris' },
] as const;

const SUPPORT_ITEMS = [
  { key: 'privacyPolicy', to: '/privacy' },
  { key: 'cookiesPolicy', to: '/cookies' },
] as const;

const COMPANY_ITEMS = [
  { key: 'about', to: '/about' },
  { key: 'coreTeam', to: '/team' },
] as const;

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <p className="w-full font-fragment-mono text-sm uppercase tracking-[0.14px] text-[#82919F]">
    {children}
  </p>
);

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Menu = ({ isOpen, onClose }: MenuProps) => {
  const navigation = useIntlayer('navigation') as Record<
    MenuNavItemKey,
    string
  >;
  const footer = useIntlayer('footer') as {
    support: {
      title: string;
      privacyPolicy: string;
      cookiesPolicy: string;
    };
    quickContact: {
      title: string;
      linkedin: string;
    };
  };
  const header = useIntlayer('header') as {
    joinTeam: string;
  };
  const t = useIntlayer('mobile-menu') as {
    navigation: string;
    company: string;
    coreTeam: string;
    careers: string;
    quickContact: string;
    linkedin: string;
    about: string;
  };
  const overlayRef = useRef<HTMLDivElement>(null);
  const prevOpen = useRef(false);
  const rafRef = useRef<number>(0);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!isOpen) setIsCompanyOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen === prevOpen.current) return;
    prevOpen.current = isOpen;

    const overlay = overlayRef.current;
    if (!overlay) return;

    cancelAnimationFrame(rafRef.current);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tileW = vw / GRID_COLS;
    const tileH = vh / GRID_ROWS;
    const total = GRID_COLS * GRID_ROWS;

    const delays = buildDelayMap(GRID_COLS, GRID_ROWS, isOpen);
    const maxDelay = Math.max(...delays);
    const visible = new Array<boolean>(total).fill(!isOpen);

    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = (now - start) / 1000;
      let changed = false;

      for (let i = 0; i < total; i++) {
        const should = isOpen ? elapsed >= delays[i] : elapsed < delays[i];
        if (visible[i] !== should) {
          visible[i] = should;
          changed = true;
        }
      }

      if (changed) {
        overlay.style.clipPath = `path("${buildClipPathD(GRID_COLS, GRID_ROWS, tileW, tileH, visible)}")`;
      }

      if (elapsed < maxDelay + 0.05) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        overlay.style.clipPath = isOpen ? 'none' : 'path("M 0 0")';
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [isOpen]);

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-40 bg-black ${isOpen ? '' : 'pointer-events-none'}`}
      style={{ clipPath: 'path("M 0 0")' }}
      aria-hidden={!isOpen}
    >
      <nav className="flex h-full flex-col gap-8 overflow-y-auto px-4 py-8 md:px-8 lg:px-20 pb-8 pt-[94px] w-full">
        <div className="flex w-full flex-col gap-[10px]">
          <SectionLabel>{t.navigation}</SectionLabel>
          <div className="flex flex-col w-full">
            {PRIMARY_NAV_ITEMS.map((item) => (
              <LocalizedLink
                key={item.key}
                to={item.to}
                className="block w-full whitespace-nowrap py-2 font-manrope text-[20px] font-normal leading-[1.4] text-white"
                onClick={onClose}
              >
                {navigation[item.key]}
              </LocalizedLink>
            ))}

            <button
              type="button"
              className="flex w-full items-center gap-2 py-2 text-left cursor-pointer"
              aria-expanded={isCompanyOpen}
              onClick={() => setIsCompanyOpen((prev) => !prev)}
            >
              <span className="font-manrope text-[20px] font-normal leading-[1.4] text-white">
                {t.company}
              </span>
              <Chevron
                className={`size-5 text-white transition-transform duration-200 ${isCompanyOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <AnimatePresence initial={false}>
              {isCompanyOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-col">
                    {COMPANY_ITEMS.map((item) => (
                      <LocalizedLink
                        key={item.key}
                        to={item.to}
                        className="flex w-full items-center gap-2 p-3"
                        onClick={onClose}
                      >
                        <span className="mt-[1px] text-white">
                          <DropdownArrow />
                        </span>
                        <span className="flex-1 font-fragment-mono text-xs font-normal leading-[1.5] text-white">
                          {t[item.key]}
                        </span>
                      </LocalizedLink>
                    ))}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex w-full flex-col gap-[10px]">
          <SectionLabel>{footer.support.title}</SectionLabel>
          <div className="flex flex-col w-full">
            {SUPPORT_ITEMS.map((item) => (
              <LocalizedLink
                key={item.key}
                to={item.to}
                className="block w-full py-2 font-manrope text-[20px] font-normal leading-[1.4] text-white"
                onClick={onClose}
              >
                {footer.support[item.key]}
              </LocalizedLink>
            ))}
          </div>
        </div>

        <div className="flex w-full flex-col gap-[10px]">
          <SectionLabel>{footer.quickContact.title}</SectionLabel>
          <div className="py-2">
            <a
              href={SOCIAL_LINKS.LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 text-xl text-white font-normal leading-[1.4]"
              style={{ fontFamily: 'var(--font-manrope)' }}
              onClick={(e) => {
                e.preventDefault();
                onClose();
                openExternal(SOCIAL_LINKS.LINKEDIN);
              }}
            >
              {t.linkedin}
            </a>
          </div>
        </div>

        <div className="flex w-full flex-col gap-[10px]">
          <SectionLabel>{t.careers}</SectionLabel>
          <div className="py-2">
            <Button
              variant="default"
              className="h-[42px] w-full justify-center border-white/16 px-6 py-[10px] text-base font-normal backdrop-blur-[4px]"
              onClick={(event) => {
                event.preventDefault();
                onClose();
                openExternal(SOCIAL_LINKS.LINKEDIN);
              }}
            >
              {header.joinTeam}
              <ArrowUpRight className="size-[14px]" />
            </Button>
          </div>
        </div>
      </nav>
    </div>
  );
};
